import {
  useGetUserMemberships,
  usePendingOrganizationInvitations,
  useUserActiveOrganization,
  useUserRole
} from "@/api";
import { Link } from "@tanstack/react-router";
import { Building, RefreshCw, Settings, SquarePlus } from "lucide-react";
import { TypographyP } from "../typography/P";
import { TypographySM } from "../typography/SM";
import TooltipHover from "../ui/TooltipHover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "../ui/dropdown";
import { NotificationBadge } from "../ui/notificationBadge";

interface Props {
  activeOrganizationSlug?: string | null | undefined;
}

export const OrganizationDropdown = ({ activeOrganizationSlug }: Props) => {
  const { activeOrganization } = useUserActiveOrganization();
  const { isAdmin } = useUserRole();
  const { data: memberships } = useGetUserMemberships();
  const { data: invitations } = usePendingOrganizationInvitations();

  const memberOfMultipleOrganizations = memberships.length > 1;

  return (
    <DropdownMenu>
      <TooltipHover text="Organization">
        <DropdownMenuTrigger asChild>
          <div
            data-testid="sidebarOrganization"
            className="relative block outline-none items-center p-1 transition-colors text-brand-gray-400 hover:text-brand-gray-900  dark:text-brand-gray-300 dark:hover:text-brand-gray-100"
          >
            <Building strokeWidth={1.6} className="h-[20px] w-[20px]" />
            {invitations.length > 0 && (
              <NotificationBadge className="absolute -right-2 -top-2">
                {invitations.length}
              </NotificationBadge>
            )}
          </div>
        </DropdownMenuTrigger>
      </TooltipHover>
      <DropdownMenuContent
        side="right"
        sideOffset={10}
        avoidCollisions
        className="mt-2"
      >
        <DropdownMenuItem className="flex flex-col justify-start p-1.5 pl-2 items-start focus:bg-transparent dark:focus:bg-transparent">
          <p className="text-brand-gray-500 text-[10px]">Active organization</p>
          <TypographyP
            data-testid="activeOrganizationName"
            className="text-brand-gray-800 font-medium text-base"
          >
            {activeOrganization?.name || "N/A"}
          </TypographyP>
        </DropdownMenuItem>
        {isAdmin && (
          <Link
            to="/organization/$organizationSlug/organization-settings"
            params={{
              organizationSlug:
                activeOrganization?.slug || activeOrganizationSlug || ""
            }}
            disabled={!isAdmin}
            data-testid="organizationSettings"
          >
            <DropdownMenuItem className="text-brand-gray-600 gap-1.5 cursor-pointer">
              <Settings size={16} />
              <TypographySM>Organization Settings</TypographySM>
            </DropdownMenuItem>
          </Link>
        )}
        <DropdownMenuSeparator />
        {memberOfMultipleOrganizations && (
          <Link to="/organization" search={{ reset: true }}>
            <DropdownMenuItem className="text-brand-gray-600 gap-1.5 cursor-pointer">
              <RefreshCw size={16} />
              <TypographySM>Switch organization</TypographySM>
            </DropdownMenuItem>
          </Link>
        )}
        {invitations.length > 0 && (
          <Link to="/invitations">
            <DropdownMenuItem className="text-brand-gray-600 gap-1.5 cursor-pointer">
              <NotificationBadge>{invitations.length}</NotificationBadge>
              <TypographySM>Pending invitations</TypographySM>
            </DropdownMenuItem>
          </Link>
        )}
        <Link to="/organization-create" search={{ redirect: undefined }}>
          <DropdownMenuItem className="text-brand-gray-600 gap-1.5 cursor-pointer">
            <SquarePlus size={16} />
            <TypographySM>Create new organization</TypographySM>
          </DropdownMenuItem>
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
